import { esc, openModal, onModalClose, closeModal, showModalErr } from '../ui/modal';
import { t } from '../ui/lang';
import { stopCloseShift } from './close-shift-shared';
import { fetchFarmFlows } from '../flows/flows-shared';
/** How often the status line asks how the run is going. */
const POLL_MS = 4000;
/** `YYYY-MM-DD` in the browser's local time — what a date input holds. */
function localDateValue(d) {
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/**
 * «Close shift» for one device: pick the app and the days, start the one-shot run that
 * films the history and sends the video to the farm's Telegram chats. The days are sent
 * as picked: they are compared with the dates the APP prints, not converted to UTC.
 */
export function openCloseShiftModal(farmId, udid) {
    const base = `/api/farms/${farmId}/clicker/devices/${encodeURIComponent(udid)}`;
    const today = localDateValue(new Date());
    const modal = openModal(`
        <h2 class="modal__title">${t('shifts.close_shift')}</h2>
        <form class="close-shift-form" novalidate>
            <div class="modal__field">
                <label>${t('shifts.app')}</label>
                <select class="settings-input cs-app" disabled><option>${t('common.loading')}</option></select>
            </div>
            <div class="modal__field">
                <label>${t('shifts.flow')}</label>
                <select class="settings-input cs-flow" disabled></select>
            </div>
            <div class="modal__field">
                <label>${t('shifts.from')}</label>
                <input type="date" class="settings-input cs-from" value="${today}" />
            </div>
            <div class="modal__field">
                <label>${t('shifts.to')}</label>
                <input type="date" class="settings-input cs-to" value="${today}" />
            </div>
            <div class="modal__error cs-error" style="display:none"></div>
            <div class="modal__actions">
                <button type="submit" class="modal__submit cs-start" disabled>${t('shifts.start')}</button>
            </div>
        </form>
        <div class="close-shift-status">
            <div class="cs-state settings-hint">${t('common.loading')}</div>
            <button type="button" class="tbl-btn delete-btn cs-stop" style="display:none">${t('shifts.stop')}</button>
        </div>
    `);
    const appSel = modal.querySelector('.cs-app');
    const flowSel = modal.querySelector('.cs-flow');
    const fromEl = modal.querySelector('.cs-from');
    const toEl = modal.querySelector('.cs-to');
    const errEl = modal.querySelector('.cs-error');
    const startBtn = modal.querySelector('.cs-start');
    const stateEl = modal.querySelector('.cs-state');
    const stopBtn = modal.querySelector('.cs-stop');
    let timer;
    let busy = false;
    let flows = [];
    // The app's close-shift flows this farm's team may run, its default one preselected —
    // the same default the device's clicker config falls back to.
    const fillFlows = () => {
        const matching = flows.filter((f) => f.enabled && f.task_type === 'shift' && f.app === appSel.value);
        const pick = matching.find((f) => f.default_pick) ?? matching[0];
        flowSel.innerHTML = matching.map((f) => `<option value="${f.id}"${f === pick ? ' selected' : ''}>${esc(f.name)}</option>`).join('');
        flowSel.disabled = matching.length < 2;
    };
    appSel.addEventListener('change', fillFlows);
    const poll = async () => {
        try {
            const apps = await fetch(`${base}/shift-apps`);
            if (apps.ok)
                busy = (await apps.json()).running;
        }
        catch { /* keep the last line */ }
        stateEl.textContent = busy ? t('shifts.state_running') : t('shifts.state_idle');
        startBtn.disabled = busy || !appSel.value;
        stopBtn.style.display = busy ? '' : 'none';
        timer = window.setTimeout(() => { void poll(); }, POLL_MS);
    };
    const stop = () => { if (timer)
        window.clearTimeout(timer); timer = undefined; };
    // Anyone who may open this modal may stop the run, not only whoever started it.
    stopBtn.addEventListener('click', async () => {
        stopBtn.disabled = true;
        if (await stopCloseShift(farmId, udid)) {
            busy = false;
            stopBtn.style.display = 'none';
            stop();
            void poll();
        }
        stopBtn.disabled = false;
    });
    onModalClose(modal, () => { stop(); closeModal(); });
    void Promise.all([fetch(`${base}/shift-apps`), fetchFarmFlows(farmId).catch(() => [])])
        .then(async ([res, list]) => {
        flows = list;
        const data = res.ok ? await res.json() : { apps: [], running: false };
        busy = data.running;
        if (!data.apps.length) {
            appSel.innerHTML = '';
            showModalErr(errEl, t('shifts.no_apps'));
            return;
        }
        appSel.innerHTML = data.apps.map((a) => `<option value="${esc(a)}">${esc(a)}</option>`).join('');
        appSel.disabled = false;
        fillFlows();
        startBtn.disabled = busy;
    })
        .catch(() => showModalErr(errEl, t('clicker.network_error')));
    void poll();
    modal.querySelector('.close-shift-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        errEl.style.display = 'none';
        if (!fromEl.value || !toEl.value || toEl.value < fromEl.value) {
            showModalErr(errEl, t('shifts.period_invalid'));
            return;
        }
        startBtn.disabled = true;
        try {
            const res = await fetch(`${base}/close-shift`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    app_name: appSel.value, shift_from: fromEl.value, shift_to: toEl.value,
                    ...(flowSel.value ? { flow_id: Number(flowSel.value) } : {}),
                }),
            });
            if (!res.ok) {
                showModalErr(errEl, (await res.json()).error ?? t('shifts.failed_to_start'));
                startBtn.disabled = false;
                return;
            }
            busy = true;
            stateEl.textContent = t('shifts.state_starting');
        }
        catch {
            showModalErr(errEl, t('clicker.network_error'));
            startBtn.disabled = false;
        }
    });
}
